import React, { useEffect, useState } from 'react';
import { Card, Badge, Table, Button, Modal, Form, Alert } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { User } from '../types';

interface Achievement {
  title: string;
  description: string;
  date: string;
  link?: string;
}

const MemberDetail: React.FC = () => {
  const { id } = useParams();
  const [member, setMember] = useState<User | null>(null);
  const [achFormVisible, setAchFormVisible] = useState(false);
  const [editingAch, setEditingAch] = useState<Achievement | null>(null);
  const [achForm, setAchForm] = useState({ title: '', description: '', date: '', link: '' });
  const [error, setError] = useState('');

  const fetchMember = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/members/${id}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json();
      setMember(data);
    } catch {
      setError('获取成员信息失败');
    }
  };

  useEffect(() => {
    fetchMember();
    // eslint-disable-next-line
  }, [id]);

  if (!member) return null;

  const handleAddAch = () => {
    setEditingAch(null);
    setAchFormVisible(true);
    setAchForm({ title: '', description: '', date: '', link: '' });
  };
  const handleEditAch = (ach: Achievement) => {
    setEditingAch(ach);
    setAchFormVisible(true);
    setAchForm({
      title: ach.title || '',
      description: ach.description || '',
      date: ach.date || '',
      link: ach.link || ''
    });
  };
  const handleDeleteAch = async (achId: string) => {
    if (!member) return;
    if (window.confirm('确定要删除该成果吗？')) {
      await fetch(`/api/members/${member._id}/achievements/${achId}`, { method: 'DELETE' });
      fetchMember();
    }
  };
  const handleAchFormOk = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!member) return;
    if (editingAch && (editingAch as any)._id) {
      await fetch(`/api/members/${member._id}/achievements/${(editingAch as any)._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(achForm),
      });
    } else {
      await fetch(`/api/members/${member._id}/achievements`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(achForm),
      });
    }
    setAchFormVisible(false);
    fetchMember();
  };

  return (
    <Card style={{ maxWidth: 600, margin: '0 auto', marginTop: 32 }}>
      <Card.Body>
        <Card.Title>{member.name}</Card.Title>
        {error && <Alert variant="danger">{error}</Alert>}
        <Table bordered size="sm">
          <tbody>
            <tr><td>性别</td><td>{member.gender}</td></tr>
            <tr><td>学号</td><td>{member.studentId}</td></tr>
            <tr><td>联系电话</td><td>{member.phone}</td></tr>
            <tr><td>邮箱</td><td>{member.email}</td></tr>
            <tr><td>所属导师</td><td>{member.supervisor}</td></tr>
            <tr><td>入学年份</td><td>{member.enrollmentYear}</td></tr>
            <tr><td>学生类型</td><td><Badge bg="info">{member.studentType}</Badge></td></tr>
            <tr><td>角色</td><td><Badge bg="secondary">{member.role}</Badge></td></tr>
          </tbody>
        </Table>
        <h5 className="mt-4">成果展示 <Button size="sm" variant="primary" onClick={handleAddAch}>新增成果</Button></h5>
        <Table bordered size="sm">
          <thead>
            <tr>
              <th>标题</th>
              <th>描述</th>
              <th>日期</th>
              <th>链接</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {(member.achievements || []).map((item: any, idx: number) => (
              <tr key={idx}>
                <td>{item.title}</td>
                <td>{item.description}</td>
                <td>{item.date ? new Date(item.date).toLocaleDateString() : ''}</td>
                <td>{item.link && <a href={item.link} target="_blank" rel="noopener noreferrer">链接</a>}</td>
                <td>
                  <Button size="sm" variant="outline-secondary" onClick={() => handleEditAch(item)}>编辑</Button>{' '}
                  <Button size="sm" variant="outline-danger" onClick={() => handleDeleteAch(item._id)}>删除</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Modal show={achFormVisible} onHide={() => setAchFormVisible(false)}>
          <Modal.Header closeButton>
            <Modal.Title>{editingAch ? '编辑成果' : '新增成果'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleAchFormOk}>
              <Form.Group className="mb-3">
                <Form.Label>标题</Form.Label>
                <Form.Control value={achForm.title} onChange={e => setAchForm({ ...achForm, title: e.target.value })} required />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>描述</Form.Label>
                <Form.Control value={achForm.description} onChange={e => setAchForm({ ...achForm, description: e.target.value })} as="textarea" />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>日期</Form.Label>
                <Form.Control type="date" value={achForm.date} onChange={e => setAchForm({ ...achForm, date: e.target.value })} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>链接</Form.Label>
                <Form.Control value={achForm.link} onChange={e => setAchForm({ ...achForm, link: e.target.value })} />
              </Form.Group>
              <Button type="submit" variant="primary">确定</Button>
            </Form>
          </Modal.Body>
        </Modal>
      </Card.Body>
    </Card>
  );
};

export default MemberDetail; 